import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { FlaskConical, Beaker, ChevronDown, ChevronRight, Link2, X, Atom, Shield, Mountain, BookOpen, CheckCircle2, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { parseAxiosError } from '@/utils/axiosErrors';
import { isProUser, isProAnalysis } from '@/lib/entitlements';
import { DesignUploadCard } from '@/components/DesignUploadCard';
import { InlineResultSummary } from '@/components/InlineResultSummary';
import GenerationMeanLanding from '@/components/GenerationMeanLanding';
import QualitativeDUSLanding from '@/components/QualitativeDUSLanding';
import SharedNav from '@/components/SharedNav';
import BreedingPipeline from '@/components/BreedingPipeline';
import StudyDesignAdvisor from '@/components/StudyDesignAdvisor';
import MutationBreedingGuide from '@/components/MutationBreedingGuide';
import { MutationBreedingPipeline, IndianCropMutationDB, MutantVarietyComparison, DUSTestingGuide } from '@/components/MutationTools';
import BiochemistryCalculator from '@/components/BiochemistryCalculator';
import BiochemLabAdvisor from '@/components/BiochemLabAdvisor';
import { PhysiologyPipeline } from '@/components/results/PhysiologyPhase3';
import PhenoScanHandoffBanner from '@/components/PhenoScanHandoffBanner';

import { SECTIONS } from '@/data/analysisCatalog';

export default function BiometricalAnalysis() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPro = isProUser(user);
  const phenoscanHandoff = location.state?.phenoscanHandoff || null;
  // Multi-section sidebar: each section's open/closed state is
  // independent so users can browse multiple groups (e.g. compare
  // Mating Designs and Stability) without losing the other group's
  // open state. (Niranjan, Feb 2026)
  // Single primary "openSection" is preserved as the section that
  // hosts the currently active design — used for narrow-screen and
  // breadcrumb-style highlights.
  const [openSection, setOpenSection] = useState('mating');
  const [openSectionsSet, setOpenSectionsSet] = useState(() => new Set(['mating']));
  const [activeDesign, setActiveDesign] = useState('diallel');
  const [analysisIds, setAnalysisIds] = useState({});
  // Sub-Batch 3R: store the full fresh-run payload (not just the id) so
  // we can render an InlineResultSummary below the upload card.
  const [freshResults, setFreshResults] = useState({});
  const [exporting, setExporting] = useState(null);
  const [showPipeline, setShowPipeline] = useState(false);
  const [showMutationGuide, setShowMutationGuide] = useState(false);
  const [cleanHandoff, setCleanHandoff] = useState(null);

  // Clean-module hand-off → pre-seed every design's upload card with the
  // cleaned CSV and expose an audit banner linking back to the cleaning
  // session. Runs once on mount; the per-design sessionStorage keys are
  // consumed (and removed) by DesignUploadCard when each design is opened.
  //
  // P2 (Niranjan, D2_sanjana.xlsx, Feb 2026): Mating Designs require
  // structural columns (Parent1/Parent2, Line/Tester, Male/Female,
  // Generation, …) that a cleaned diversity panel doesn't carry.
  // Auto-seeding the file silently let the user click Run on a Diallel
  // card with a Genotype-only dataset. We now schema-check each design
  // against the cleaned column list and write a `bk_schema_mismatch_*`
  // marker for the mismatched ones — DesignUploadCard reads the marker
  // and renders an explanatory banner in place of the auto-seeded file.
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem('bk_clean_handoff');
      if (!raw) return;
      const ho = JSON.parse(raw);
      if (!ho?.csv_b64 || !ho?.filename) return;
      // Decode the base64 CSV once
      const binary = atob(ho.csv_b64);
      // Extract the column list from the CSV header row (first line)
      // for schema-aware seeding decisions.
      let cleanedColumns = [];
      try {
        const firstNewline = binary.search(/\r?\n/);
        const headerLine = (firstNewline >= 0 ? binary.slice(0, firstNewline) : binary).trim();
        cleanedColumns = headerLine.split(',').map(s => s.replace(/^\s+|\s+$/g, '').replace(/^"|"$/g, ''));
      } catch { /* best-effort parse — empty list = no schema check */ }

      setCleanHandoff({
        job_id: ho.job_id,
        filename: ho.filename,
        timestamp: ho.timestamp,
        n_rows: ho.n_rows,
        n_cols: ho.n_cols,
        cleaning_log_summary: ho.cleaning_log_summary,
        cleanedColumns,
      });

      // Schema match: case-insensitive exact match per required column.
      const lowerSet = new Set(cleanedColumns.map(c => c.toLowerCase()));
      const matches = (req) => req.every(c => lowerSet.has(c.toLowerCase()));
      const checkSchema = (design) => {
        if (!design.requiredColumns) return true; // designs without a schema are universally compatible
        if (matches(design.requiredColumns)) return true;
        if (design.altRequiredColumns && matches(design.altRequiredColumns)) return true;
        return false;
      };

      // Write per-design handoff keys so DesignUploadCard auto-ingests.
      const perDesign = JSON.stringify({ csv: binary, filename: ho.filename });
      for (const section of SECTIONS) {
        for (const design of section.designs) {
          if (design.noUpload) continue;
          // Always clear any stale mismatch marker from previous sessions
          try { sessionStorage.removeItem(`bk_schema_mismatch_${design.key}`); } catch { /* ignore */ }
          if (checkSchema(design)) {
            try {
              sessionStorage.setItem(`bk_handoff_${design.key}`, perDesign);
            } catch { /* quota — ignore per-design */ }
          } else {
            // Schema mismatch: do NOT auto-seed the file. Surface a
            // marker so the card can render an explanatory banner.
            const required = design.requiredColumns || [];
            try {
              sessionStorage.setItem(
                `bk_schema_mismatch_${design.key}`,
                JSON.stringify({
                  required,
                  altRequired: design.altRequiredColumns || null,
                  cleanedColumns,
                  filename: ho.filename,
                  n_cols: ho.n_cols,
                }),
              );
            } catch { /* ignore */ }
          }
        }
      }
      // Keep `bk_clean_handoff` so the audit banner survives navigations
      // within Analysis; it is cleared only by an explicit dismiss.
    } catch (e) {
      // Malformed handoff — ignore silently
    }
  }, []);

  // PhenoScan hand-off → auto-switch to requested analysis
  React.useEffect(() => {
    if (!phenoscanHandoff?.target) return;
    const map = {
      path_analysis: 'path', pca: 'pca-bio', d_squared: 'd2-cluster',
      heritability: 'heritability', selection_index: 'selection-index',
      ammi: 'ammi', gge_biplot: 'gge',
    };
    const key = map[phenoscanHandoff.target];
    if (!key) return;
    const found = SECTIONS.flatMap(s => s.designs).find(d => d.key === key);
    if (found) {
      setActiveDesign(key);
      const parent = SECTIONS.find(s => s.designs.some(d => d.key === key));
      if (parent) {
        setOpenSection(parent.id);
        setOpenSectionsSet(prev => new Set([...prev, parent.id]));
      }
    }
  }, [phenoscanHandoff]);

  // Deep-link support: /biometrical-analysis?section=stability&design=ammi
  React.useEffect(() => {
    const applyQuery = () => {
      try {
        const qs = new URLSearchParams(window.location.search);
        const section = qs.get('section');
        const designKey = qs.get('design');
        if (section && SECTIONS.some(s => s.id === section)) {
          setOpenSection(section);
          setOpenSectionsSet(prev => new Set([...prev, section]));
        }
        if (designKey) {
          const found = SECTIONS.flatMap(s => s.designs).find(d => d.key === designKey);
          if (found) {
            setActiveDesign(designKey);
            const parent = SECTIONS.find(s => s.designs.some(d => d.key === designKey));
            if (parent) {
              setOpenSection(parent.id);
              setOpenSectionsSet(prev => new Set([...prev, parent.id]));
            }
          }
        }
      } catch {}
    };
    applyQuery();
    window.addEventListener('popstate', applyQuery);
    return () => window.removeEventListener('popstate', applyQuery);
  }, []);

  const handleResult = (data, designKey) => {
    if (data?.id) {
      setAnalysisIds(prev => ({ ...prev, [designKey]: data.id }));
      setFreshResults(prev => ({ ...prev, [designKey]: data }));
    }
  };

  const handleExport = async (analysisId, format) => {
    // Exports are Pro-only (Free = web preview). Nudge instead of hitting 402.
    if (!isPro) {
      toast.info('Exports are a Pro feature. Upgrade to download PDF / Excel / CSV.', {
        action: { label: 'Upgrade', onClick: () => navigate('/pricing') },
      });
      return;
    }
    const key = `${analysisId}-${format}`;
    setExporting(key);
    toast.info(`Generating ${format.toUpperCase()}...`);
    try {
      const response = await axios.post(`${API}/generate-document/${analysisId}`, { format },
        // B-NEW-3 FIX-3A: 90 s ceiling so a hung request can't
        // permanently disable the export buttons. The `finally`
        // block below always resets `exporting` to null.
        // Bumped from 30 s in Sub-Batch 3W (Jun 2026) for large DOCX
        // payloads (General Analysis with many figures).
        { withCredentials: true, responseType: 'blob', timeout: 90000 });
      const mimeMap = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      };
      const blobType = mimeMap[format] || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BKAgriD_Report.${format}`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded!`);
    } catch (e) {
      // B-NEW-2/3: blob 4xx/5xx may carry JSON detail; surface it.
      if (e?.code === 'ECONNABORTED') {
        toast.error('Export timed out. Please try again. If the issue persists, refresh the page (Ctrl+Shift+R).');
      } else {
        const detail = await parseAxiosError(e, 'Export failed.');
        toast.error(detail);
      }
    }
    finally { setExporting(null); }
  };

  const toggleSection = (sectionId) => {
    // Multi-section accordion: clicking toggles a section
    // independently, leaving other open sections untouched.
    setOpenSectionsSet(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
    // If the user clicked an already-open section, do not change
    // the active design. If they opened a new one, select its
    // first design and mark it as the primary open section.
    if (!openSectionsSet.has(sectionId)) {
      setOpenSection(sectionId);
      const first = SECTIONS.find(s => s.id === sectionId)?.designs[0];
      if (first) setActiveDesign(first.key);
    }
  };

  const currentDesign = SECTIONS.flatMap(s => s.designs).find(d => d.key === activeDesign);

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }} data-testid="biometrical-analysis-page">
      <SharedNav />

      <div className="max-w-[1320px] mx-auto px-6 page-enter" style={{ paddingTop: 32, paddingBottom: 48 }}>
        {/* Page Header */}
        <div className="mb-6" style={{ borderLeft: '4px solid var(--green-500)', paddingLeft: 16 }}>
          <h1 className="font-display flex items-center gap-2" style={{ fontSize: 28, color: 'var(--green-900)' }}>
            <FlaskConical size={24} style={{ color: 'var(--green-500)' }} />
            Biometrical Analysis
          </h1>
          <p className="font-body mt-1" style={{ fontSize: 15, color: 'var(--text-medium)' }}>
            Select an analysis category, then upload your data for the specific analysis.
          </p>
        </div>

        {/* PhenoScan hand-off banner */}
        <PhenoScanHandoffBanner handoff={phenoscanHandoff}
          targetLabel={SECTIONS.flatMap(s => s.designs).find(d => d.key === activeDesign)?.label || 'Analysis'} />

        {/* Clean-module hand-off audit banner */}
        {cleanHandoff && (
          <div
            data-testid="clean-handoff-banner"
            className="rounded-xl p-4 mb-6 flex items-center justify-between"
            style={{
              background: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)',
              border: '1.5px solid #10B981',
            }}
          >
            <div className="flex items-start gap-3 min-w-0">
              <CheckCircle2 size={20} style={{ color: '#047857' }} className="shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-body text-sm font-bold" style={{ color: '#065F46' }}>
                  Using cleaned data from {new Date(cleanHandoff.timestamp).toLocaleString()}
                </p>
                <p className="font-body text-xs mt-0.5" style={{ color: '#047857' }}>
                  <span className="font-mono">{cleanHandoff.filename}</span>
                  {` · ${cleanHandoff.n_rows} rows × ${cleanHandoff.n_cols} cols`}
                  {cleanHandoff.cleaning_log_summary?.rows_removed
                    ? ` · ${cleanHandoff.cleaning_log_summary.rows_removed} row(s) removed`
                    : ''}
                  {cleanHandoff.cleaning_log_summary?.cells_to_nan
                    ? ` · ${cleanHandoff.cleaning_log_summary.cells_to_nan} cell(s) → NaN`
                    : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate(`/data-cleaning?job_id=${cleanHandoff.job_id}`)}
                className="flex items-center gap-1.5 font-body btn-press transition-colors"
                style={{
                  fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)',
                  padding: '6px 12px', border: '1.5px solid #047857',
                  color: '#047857', background: 'white',
                }}
                data-testid="clean-handoff-view-session"
              >
                <BookOpen size={12} /> View cleaning session
              </button>
              <button
                onClick={() => {
                  sessionStorage.removeItem('bk_clean_handoff');
                  setCleanHandoff(null);
                }}
                className="flex items-center font-body btn-press transition-colors"
                style={{
                  fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)',
                  padding: '6px 10px', border: '1px solid #A7F3D0',
                  color: '#065F46', background: 'white',
                }}
                data-testid="clean-handoff-dismiss"
                title="Dismiss banner (cleaned data stays loaded for this session)"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Pipeline Banner */}
        {!showPipeline && (
          <div className="rounded-xl p-4 mb-6 flex items-center justify-between fade-in" style={{ background: 'linear-gradient(135deg, #1B4332, #2D6A4F)', boxShadow: '0 4px 16px rgba(27,67,50,0.2)' }} data-testid="pipeline-banner-bio">
            <div>
              <p className="font-body text-sm font-bold text-white">Run Complete Breeding Pipeline</p>
              <p className="font-body text-xs text-white/70">ANOVA + Heritability + Correlation + Path + Selection Index + Master Report — one click</p>
            </div>
            <button onClick={() => setShowPipeline(true)}
              className="flex items-center gap-2 font-body btn-press shrink-0"
              style={{ fontSize: 13, fontWeight: 700, padding: '10px 20px', borderRadius: 'var(--radius-md)', background: 'white', color: '#1B4332' }}
              data-testid="open-pipeline-btn-bio">
              <Link2 size={15} /> Launch Pipeline
            </button>
          </div>
        )}
        {showPipeline && (
          <div className="bg-white rounded-2xl p-6 mb-6" style={{ border: '2px solid var(--green-500)', boxShadow: '0 8px 32px rgba(27,67,50,0.12)' }}>
            <div className="flex items-center justify-between mb-4">
              <span />
              <button onClick={() => setShowPipeline(false)} className="p-1.5 rounded-lg hover:bg-gray-100 transition"><X size={16} style={{ color: 'var(--text-light)' }} /></button>
            </div>
            <BreedingPipeline />
          </div>
        )}

        <div className="flex gap-6">
          {/* Left sidebar */}
          <div className="w-[280px] shrink-0 space-y-1" data-testid="bio-sections-sidebar">
            {SECTIONS.map(section => {
              const isOpen = openSectionsSet.has(section.id);
              return (
                <div key={section.id} data-testid={`bio-section-${section.id}`}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center gap-2 font-body transition-all"
                    style={{
                      padding: '10px 14px', borderRadius: 'var(--radius-md)', textAlign: 'left',
                      fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em',
                      background: isOpen ? 'white' : 'transparent',
                      color: isOpen ? section.color : 'var(--text-medium)',
                      border: isOpen ? '1px solid var(--border-color)' : '1px solid transparent',
                      boxShadow: isOpen ? 'var(--shadow-sm)' : 'none',
                    }}
                    data-testid={`bio-section-btn-${section.id}`}
                  >
                    <ChevronDown
                      size={14}
                      style={{
                        transition: 'transform 0.2s',
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      }}
                    />
                    {section.icon === 'atom' && <Atom size={13} style={{ marginRight: 2, color: section.color }} />}
                    {section.icon === 'shield' && <Shield size={13} style={{ marginRight: 2, color: section.color }} />}
                    {section.icon === 'flask' && <FlaskConical size={13} style={{ marginRight: 2, color: section.color }} />}
                    {section.icon === 'mountain' && <Mountain size={13} style={{ marginRight: 2, color: section.color }} />}
                    {section.label}
                  </button>
                  {isOpen && (
                    <div className="ml-2 mt-1 space-y-0.5 fade-in">
                      {section.designs.map((d, idx) => {
                        const isActive = activeDesign === d.key;
                        const activeColor = section.activeColor || section.color;
                        const prev = section.designs[idx - 1];
                        // Insert a subtle family-divider when a design's
                        // `family` differs from the previous entry. The
                        // Mating Designs section uses this to visually
                        // group Diallel (I-IV) → Line × Tester →
                        // NC Designs (I-III) → Generation Mean. Plain
                        // 1-px hair-line, no label clutter, just optical
                        // breathing room. (Niranjan, Feb 2026)
                        const showDivider = d.family && prev && prev.family !== d.family;
                        return (
                          <React.Fragment key={d.key}>
                            {showDivider && (
                              <div
                                className="my-1.5 mx-3 h-px"
                                style={{ background: '#E8E6E1' }}
                                data-testid={`bio-family-divider-${d.family}`}
                                aria-hidden="true"
                              />
                            )}
                            <button
                              onClick={() => {
                                if (isProAnalysis(d.key) && !isPro) {
                                  toast.info(`${d.label} is a Pro feature.`, {
                                    description: 'Upgrade to Pro to run advanced modules.',
                                    action: { label: 'Upgrade', onClick: () => navigate('/pricing') },
                                  });
                                  return;
                                }
                                setActiveDesign(d.key);
                              }}
                              className="w-full flex items-center gap-2 font-body transition-all"
                              style={{
                                padding: '7px 12px 7px 24px', borderRadius: 'var(--radius-sm)',
                                fontSize: 13, fontWeight: isActive ? 600 : 400,
                                background: isActive ? `${activeColor}15` : 'transparent',
                                color: isActive ? activeColor : 'var(--text-dark)',
                                borderLeft: isActive ? `3px solid ${activeColor}` : '3px solid transparent',
                              }}
                              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F0F7F3'; }}
                              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                              data-testid={`bio-tab-${d.key}`}
                            >
                              <Beaker size={12} /> <span className="flex-1 text-left truncate">{d.label}</span>
                              {isProAnalysis(d.key) && !isPro && (
                                <Lock size={11} style={{ color: '#B0862F', flexShrink: 0 }} data-testid={`bio-pro-lock-${d.key}`} />
                              )}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right content */}
          <div className="flex-1 min-w-0">
            {openSection === 'stability' && <StudyDesignAdvisor />}
            {openSection === 'mutation' && (
              <div className="rounded-xl p-4 mb-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #FFFBEB, #FEF3C7)', border: '1px solid #FCD34D' }} data-testid="mutation-guide-banner">
                <div className="flex items-center gap-2">
                  <Atom size={16} style={{ color: '#B45309' }} />
                  <div>
                    <div className="font-body" style={{ fontSize: 14, fontWeight: 700, color: '#78350F' }}>
                      Mutation Breeding Guide
                    </div>
                    <div className="font-body" style={{ fontSize: 12, color: '#92400E' }}>
                      M1-M2-M3 scheme · physical vs chemical mutagens · ICAR guidelines · standard doses for Indian crops
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowMutationGuide(true)}
                  className="flex items-center gap-2 font-body btn-press shrink-0"
                  style={{ fontSize: 13, fontWeight: 700, padding: '8px 16px', borderRadius: 'var(--radius-md)', background: '#B45309', color: 'white' }}
                  data-testid="open-mutation-guide-btn"
                >
                  <BookOpen size={14} /> Open Guide
                </button>
              </div>
            )}
            {currentDesign && (
              <div className="bg-white rounded-2xl fade-in" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
                <div className="p-6 pb-0">
                  <div className="flex items-center gap-3 mb-5" data-testid={`bio-title-${currentDesign.key}`}>
                    <div className="flex items-center justify-center font-body" style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: currentDesign.color, color: 'white', fontSize: 18, fontWeight: 700,
                    }}>
                      {currentDesign.label.charAt(0)}
                    </div>
                    <div>
                      <div className="font-body" style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-dark)' }}>{currentDesign.label}</div>
                      <div className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>{currentDesign.desc}</div>
                    </div>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  {currentDesign.tool === 'pipeline' ? <MutationBreedingPipeline /> :
                   currentDesign.tool === 'crop-db' ? <IndianCropMutationDB /> :
                   currentDesign.tool === 'variety-comparison' ? <MutantVarietyComparison /> :
                   currentDesign.tool === 'dus-guide' ? <DUSTestingGuide /> :
                   currentDesign.tool === 'biochem-calc' ? <BiochemistryCalculator /> :
                   currentDesign.tool === 'physio-pipeline' ? <PhysiologyPipeline /> :
                   currentDesign.tool === 'biochem-advisor' ? <BiochemLabAdvisor /> :
                   (
                    <>
                      {currentDesign.key === 'gma' && (
                        <GenerationMeanLanding
                          design={currentDesign}
                          onLoadSample={async (sampleKey) => {
                            // The DesignUploadCard reads the design.key
                            // for sample fetches. We pre-stash a session
                            // marker so the upload card auto-ingests this
                            // chosen sample, then trigger its built-in
                            // sample loader. Niranjan, Feb 2026.
                            try {
                              sessionStorage.setItem(
                                `bk_genmean_sample_${currentDesign.key}`,
                                sampleKey,
                              );
                              // Soft scroll to upload card
                              const uploadEl = document.querySelector('[data-testid^="upload-zone-"]');
                              if (uploadEl) uploadEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            } catch { /* ignore */ }
                          }}
                        />
                      )}
                      {currentDesign.key === 'qualitative-dus' && (
                        <QualitativeDUSLanding designKey={currentDesign.key} />
                      )}
                      <DesignUploadCard
                        key={currentDesign.key}
                        design={currentDesign}
                        onResult={(data) => handleResult(data, currentDesign.key)}
                        analysisId={analysisIds[currentDesign.key]}
                        exporting={exporting}
                        onExport={handleExport}
                      />
                      {/* Sub-Batch 3R Layer 3: inline result summary
                          so fresh Association / Multivariate runs show
                          content immediately below the trigger (canonical
                          inline-below-trigger pattern). */}
                      {freshResults[currentDesign.key] && (
                        <InlineResultSummary
                          data={freshResults[currentDesign.key]}
                          designKey={currentDesign.key}
                          designLabel={currentDesign.label}
                          color="#2D6A4F"
                        />
                      )}
                    </>
                   )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <MutationBreedingGuide open={showMutationGuide} onClose={() => setShowMutationGuide(false)} />
    </div>
  );
}
